import { ArcElement, Chart } from 'chart.js';
import parse from 'html-react-parser';
import { useCallback, useId, useRef, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';

import type { CalculationData } from '~/api/common/types';

import { Card, Collapsible, StatusTag, Tabs } from '~/components';
import { useIntl } from '~/hooks/useIntl';
import { useRerender } from '~/hooks/useRerender';
import { clsx } from '~/styles/utils';

import type { CalculationDataWithAdvantage, AdvantageSimulationChartProps as Props } from './AdvantageSimulationDialog.types';

import {
  chartContainerStyle,
  chartItemStyle,
  chartStyle,
  detailsDescriptionStyle,
  detailsDisclaimerStyle,
  detailsHeaderStyle,
  detailsStyle,
  detailsTitleStyle,
  detailsTitleWrapperStyle,
  dialogStyle,
  hiddenStyle,
  legendButtonStyle,
  legendDotColorStyles,
  legendDotStyle,
  legendItemActiveStyle,
  legendItemHiddenStyle,
  legendItemStyle,
  legendLabelStyle,
  legendStyle,
  totalsItemStyle,
  totalsLabelStyle,
  totalsStyle,
  totalsValueStyle,
  totalsValueWrapperStyle,
} from './AdvantageSimulationChart.css';
import { useChartConfig, useChartData } from './AdvantageSimulationDialog.hooks';

Chart.register(ArcElement);

export const AdvantageSimulationChart = ({ calculationData }: Props) => {
  assertCalculationDataHasAdvantage(calculationData);

  const chartRef = useRef<Chart<'doughnut', number[]>>(null);
  const chartId = useId();

  const [showDataWithCs, setShowDataWithCs] = useState(true);
  const { dataWithCs, dataWithoutCs } = useChartData(calculationData);
  const data = showDataWithCs ? dataWithCs : dataWithoutCs;
  const config = useChartConfig(data);

  const activeElement = chartRef.current?.getActiveElements()[0] ?? null;
  const activeSlice = activeElement ? data[activeElement.index] : null;

  const rerender = useRerender();
  const { formatCurrency } = useIntl();
  const { t } = useTranslation(['invoices']);

  const onLabelClick = useCallback(
    (index: number) => {
      const chart = chartRef.current;

      if (!chart) return;

      if (chart.getActiveElements().some((activeElement) => activeElement.index === index)) {
        chart.setActiveElements([]);
      } else {
        chart.setActiveElements([{ datasetIndex: 0, index }]);
      }

      chart.update();

      rerender(); // Rerender is needed to update the UI
    },
    [rerender],
  );

  return (
    <div className={dialogStyle}>
      <Tabs.Root onValueChange={(value) => setShowDataWithCs(value === 'with-cs')} value={showDataWithCs ? 'with-cs' : 'without-cs'}>
        <Tabs.List>
          <Tabs.Tab aria-controls={chartId} value="with-cs">
            {t('invoices:createEdit.advantageSimulationModal.toggle.withCs')}
          </Tabs.Tab>

          <Tabs.Tab aria-controls={chartId} value="without-cs">
            {t('invoices:createEdit.advantageSimulationModal.toggle.withoutCs')}
          </Tabs.Tab>
        </Tabs.List>
      </Tabs.Root>

      <div className={totalsStyle}>
        <div className={totalsItemStyle}>
          <p className={totalsLabelStyle}>{t('invoices:createEdit.advantageSimulationModal.totals.totalInvoiced')}</p>

          <p className={totalsValueStyle}>{formatCurrency(calculationData.coExclVat)}</p>
        </div>

        <div className={totalsItemStyle}>
          <p className={totalsLabelStyle}>{t('invoices:createEdit.advantageSimulationModal.totals.netAmount')}</p>

          <p className={totalsValueStyle}>
            {formatCurrency(showDataWithCs ? calculationData.advantageWithCs.netAmount : calculationData.advantageWithoutCs.netAmount)}
          </p>
        </div>

        <div className={clsx(totalsItemStyle, !showDataWithCs && hiddenStyle)}>
          <p className={totalsLabelStyle}>{t('invoices:createEdit.advantageSimulationModal.totals.advantage')}</p>

          {calculationData.advantageWithCs.advantage > 0 ? (
            <div className={totalsValueWrapperStyle}>
              <p className={totalsValueStyle}>{formatCurrency(calculationData.advantageWithCs.advantage)}</p>
            </div>
          ) : (
            <StatusTag icon="CloseSmall" label={t('invoices:createEdit.advantageSimulationModal.totals.noAdvantage')} status="danger" />
          )}
        </div>
      </div>

      <div className={chartStyle} id={chartId}>
        {/* Dedicated container for Doughnut chart. The chart must be the only child of this element. */}
        <div className={clsx(chartItemStyle, chartContainerStyle)}>
          <Doughnut
            data={config.data}
            options={{
              ...config.options,
              onClick: (event, elements, chart) => {
                const selectedElement = elements[0];

                if (!selectedElement || chart.getActiveElements().some((activeElement) => activeElement.index === selectedElement.index)) {
                  chart.setActiveElements([]);
                } else {
                  chart.setActiveElements([{ datasetIndex: 0, index: selectedElement.index }]);
                }

                rerender(); // Rerender is needed to update the UI
              },
            }}
            ref={chartRef}
          />
        </div>

        <ul className={clsx(chartItemStyle, legendStyle)}>
          <li className={legendLabelStyle}>
            {showDataWithCs
              ? t('invoices:createEdit.advantageSimulationModal.label.withCs')
              : t('invoices:createEdit.advantageSimulationModal.label.withoutCs')}
          </li>

          {data.map(({ id, label, value }, i) => {
            if (value === 0) return null;

            return (
              <li
                className={clsx(
                  legendItemStyle,
                  !!chartRef.current && chartRef.current.getActiveElements()[0]?.index === i && legendItemActiveStyle,
                )}
                key={id}
                onClick={() => onLabelClick(i)}
              >
                <span className={clsx(legendDotStyle, legendDotColorStyles[id])} />

                <button className={legendButtonStyle}>{label}</button>
              </li>
            );
          })}

          {/* Fill legend with invisible to prevent layout shift */}
          {data
            .filter(({ value }) => value === 0)
            .map(({ id, label }) => (
              <li className={clsx(legendItemStyle, legendItemHiddenStyle)} key={id}>
                <span className={legendDotStyle} />

                <span>{label}</span>
              </li>
            ))}
        </ul>
      </div>

      <Collapsible>
        {activeSlice && activeSlice.value > 0 && (
          <Card.Root className={detailsStyle}>
            <header className={detailsHeaderStyle}>
              <div className={detailsTitleWrapperStyle}>
                <span className={clsx(legendDotStyle, legendDotColorStyles[activeSlice.id])} />

                <span className={detailsTitleStyle}>{activeSlice.label}</span>
              </div>

              <span>{formatCurrency(activeSlice.value * (activeSlice.id === 'netAmount' ? 1 : -1))}</span>
            </header>

            <div className={detailsDescriptionStyle}>
              <span>{parse(activeSlice.description)}</span>

              {activeSlice.disclaimer && <span>*</span>}
            </div>

            {activeSlice.disclaimer && <div className={detailsDisclaimerStyle}>*{parse(activeSlice.disclaimer)}</div>}
          </Card.Root>
        )}
      </Collapsible>
    </div>
  );
};

function assertCalculationDataHasAdvantage(calculationData: CalculationData): asserts calculationData is CalculationDataWithAdvantage {
  invariant(calculationData.advantageWithCs);
  invariant(calculationData.advantageWithoutCs);
}
